import usePopupState from '@swarm/core/hooks/state/usePopupState'
import { ChildrenProps } from '@swarm/types'
import { ExtendedSecurity } from '@swarm/types/normalized-entities'
import { createContext, useCallback, useContext, useState } from 'react'

import StakeSecurityPopup, { SuccessStakeValue } from './StakeSecurityPopup'
import StakeSecuritySuccessPopup from './StakeSecuritySuccessPopup'

interface StakingCreationContextType {
  openStakeSecurityPopup(security: ExtendedSecurity): void
}

export const StakingCreationContext = createContext<StakingCreationContextType>(
  {
    openStakeSecurityPopup() {
      throw new Error(
        "Can't call openStakeSecurityPopup outside of StakingCreationContext",
      )
    },
  },
)

export const StakingCreationContextProvider = ({ children }: ChildrenProps) => {
  const [selectedSecurity, setSelectedSecurity] =
    useState<ExtendedSecurity | null>(null)
  const [successStakeValue, setSuccessStakeValue] =
    useState<SuccessStakeValue | null>(null)

  const stakeSecurityPopup = usePopupState(false)
  const stakeSecuritySuccessPopup = usePopupState(true)

  const handleSuccessStake = useCallback(
    (value: SuccessStakeValue) => {
      setSuccessStakeValue(value)
      stakeSecuritySuccessPopup.open()
    },
    [stakeSecuritySuccessPopup],
  )

  const handleCloseSuccessPopup = useCallback(() => {
    setSuccessStakeValue(null)
    stakeSecuritySuccessPopup.close()
  }, [stakeSecuritySuccessPopup])

  const handleCloseSecurityPopup = useCallback(() => {
    setSelectedSecurity(null)
    stakeSecurityPopup.close()
  }, [stakeSecurityPopup])

  const openStakeSecurityPopup = useCallback(
    (security: ExtendedSecurity) => {
      setSelectedSecurity(security)
      stakeSecurityPopup.open()
    },
    [stakeSecurityPopup],
  )

  return (
    <StakingCreationContext.Provider
      value={{
        openStakeSecurityPopup,
      }}
    >
      {children}
      <StakeSecurityPopup
        isOpen={stakeSecurityPopup.isOpen}
        onClose={handleCloseSecurityPopup}
        onSuccess={handleSuccessStake}
        securityToken={selectedSecurity}
      />
      {successStakeValue && (
        <StakeSecuritySuccessPopup
          isOpen={stakeSecuritySuccessPopup.isOpen}
          onClose={handleCloseSuccessPopup}
          stakedAmount={successStakeValue?.stakedAmount}
          stakedSecurity={successStakeValue?.securityToken}
          tx={successStakeValue?.tx}
        />
      )}
    </StakingCreationContext.Provider>
  )
}

export const useStakingCreationContext = () =>
  useContext(StakingCreationContext)
